<template>
  <div class="detail-wrap">
    <div class="detail">
      <!--tabs-->
      <div class="tab-group">
        <el-tabs v-model="activeName">
          <el-tab-pane v-for="item in editableTabs" :key="item.key" :label="item.label" :name="item.key"></el-tab-pane>
        </el-tabs>
      </div>
      <!--基本信息-->
      <custom-detail v-loading="loading" styleType="singleRow" :detailData="baseInfo"></custom-detail>
    </div>
  </div>
</template>

<script setup>
import dayjs from 'dayjs'
import CustomDetail from '@/components/CustomDetail/index'
import { getNotificationDetail } from '@/api/alarmAdmin/notification'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
const { t } = useI18n()
const loading = ref(false)
const activeName = ref('1')
const editableTabs = [
  {
    key: '1',
    label: t('host.overview')
  }
]
const baseInfo = ref([
  {
    title: t('resources.baseInfo'),
    content: [
      {
        key: t('alarm.notification_name'),
        name: 'name',
        value: ''
      },
      {
        key: t('alarm.status'),
        name: 'display_status',
        value: ''
      },
      {
        key: 'ID',
        name: 'id',
        value: ''
      },
      {
        key: t('table.created_at'),
        name: 'created_at',
        value: ''
      },
      {
        key: t('noticeSetting.mfa_channel'),
        name: 'display_sys_email_name',
        value: ''
      },
      {
        key: t('alarm.associated_alarm'),
        name: 'alarms',
        value: ''
      },
      {
        key: t('table.description'),
        name: 'description',
        value: ''
      }
    ]
  },
  {
    title: t('alarm.notice_emails'),
    content: [
      {
        name: 'notice_emails',
        value: '',
        valueTooltipHide: true,
        style: {
          width: '100%'
        },
        valueStyle: {
          width: '100%',
          display: 'block'
        }
      }
    ]
  },
  {
    title: t('alarm.phones'),
    content: [
      {
        name: 'notice_phones',
        value: '',
        valueTooltipHide: true,
        style: {
          width: '100%'
        },
        valueStyle: {
          width: '100%',
          display: 'block'
        }
      }
    ]
  },
  {
    title: t('alarm.notification_webhook'),
    content: [
      {
        name: 'notice_webhooks',
        value: '',
        valueTooltipHide: true,
        style: {
          width: '100%'
        },
        valueStyle: {
          width: '100%',
          display: 'block'
        }
      }
    ]
  }
])
const getNotificationDetailFun = async (id) => {
  loading.value = true
  try {
    const res = await getNotificationDetail(id)
    const notificationData = res.data.notification
    notificationData.created_at = notificationData.created_at ? dayjs(notificationData.created_at).format('YYYY-MM-DD HH:mm:ss') : ''
    for (const section of baseInfo.value) {
      for (const item of section.content) {
        const name = item.name
        if (name === 'display_status') {
          item.value = `<i class="${notificationData.status}"></i>${notificationData[name]}`
        } else if (name === 'notice_emails' && Array.isArray(notificationData.emails) && notificationData.emails.length) {
          item.value = buildNotifyListHtml(notificationData.emails)
        } else if (name === 'notice_phones' && Array.isArray(notificationData.phones) && notificationData.phones.length) {
          item.value = buildNotifyListHtml(notificationData.phones.map(phoneItem => phoneItem.phone || '-'))
        } else if (name === 'notice_webhooks' && Array.isArray(notificationData.webhooks) && notificationData.webhooks.length) {
          let html = '<div class="webhook-wrap">'
          for (const webhook of notificationData.webhooks) {
            const platformLabel = webhook?.platform === 'generic' ? t('alarm.generic_webhook') : (webhook?.platform || '-')
            const webhookUrl = webhook?.url || '-'
            html += `<div class="webhook-item"><div class="webhook-platform">${platformLabel}</div><div class="webhook-url">${webhookUrl}</div></div>`
          }
          html += '</div>'
          item.value = html
        } else if (name === 'alarms' && Array.isArray(notificationData.alarms) && notificationData.alarms.length) {
          let html = '<div class="list-wrap">'
          for (const i of notificationData.alarms) {
            html += `<span class="list">${i.name}</span>`
          }
          html += '</div>'
          item.value = html
        } else if (name === 'notice_emails' || name === 'notice_phones' || name === 'notice_webhooks') {
          item.value = '-'
        } else {
          item.value = notificationData[name]
        }
      }
    }
    baseInfo.value.forEach((section, sectionIndex) => {
      // 通知邮箱/手机号/Webhook 作为独立分块，空值也保留展示 '-'
      if (sectionIndex >= 1) return
      section.content = section.content.filter(item => !!item.value && !!item.value.length)
    })
    loading.value = false
  } catch (error) {
    loading.value = false
  }
}
defineExpose({
  getNotificationDetailFun
})

const buildNotifyListHtml = (items = []) => {
  let html = '<div class="notify-wrap">'
  for (const text of items) {
    html += `<div class="notify-item">${text || '-'}</div>`
  }
  html += '</div>'
  return html
}
</script>

<style lang="scss" scoped>
:deep(.webhook-wrap) {
  width: 100%;
  background: #f5f6f8;
  border-radius: 2px;
  padding: 8px 12px;
}

:deep(.webhook-item) {
  padding: 2px 0 6px;
  border-bottom: 1px dashed #e4e7ed;
  margin-bottom: 6px;
}

:deep(.webhook-item:last-child) {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

:deep(.webhook-platform) {
  font-size: 12px;
  line-height: 20px;
  color: #909399;
}

:deep(.webhook-url) {
  font-size: 14px;
  line-height: 22px;
  color: #303133;
  word-break: break-all;
}

:deep(.notify-wrap) {
  width: 100%;
  background: #f5f6f8;
  border-radius: 2px;
  padding: 8px 12px;
}

:deep(.notify-item) {
  font-size: 14px;
  line-height: 22px;
  color: #303133;
  word-break: break-all;
  padding: 2px 0 6px;
  border-bottom: 1px dashed #e4e7ed;
  margin-bottom: 6px;
}

:deep(.notify-item:last-child) {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
</style>